import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";

import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  flex-grow: 1;
  max-width: 300px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background-color: #155e36; }
  svg { margin-right: 6px; }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 2;
  }

  th { background: #87ceeb; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  tr:hover { background-color: #eef6ff; }

  .positive { background-color: #e7f9ed; color: #2e7d32; font-weight: 500; }
  .negative { background-color: #ffeaea; color: #c62828; font-weight: 500; }
  .bold { font-weight: bold; }

  .rank-up { color: green; font-weight: bold; }
  .rank-down { color: red; font-weight: bold; }
  .rank-neutral { color: #666; }

  .highlight-top { background-color: rgba(0, 128, 0, 0.1); font-weight: bold; }
  .highlight-low { background-color: rgba(255, 0, 0, 0.1); font-weight: bold; }
  .highlight-neutral { background-color: #f0f0f0; }
`;

const monthNamesPL = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień",
];

const SalesByContractorTable = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // 0..11 or "full"
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState("YTD");
  const [sortOrder, setSortOrder] = useState("desc");
  const rowsPerPage = 10;

  const latestDate = useMemo(() => {
    const dates = data.map((i) => new Date(i.DataWystawienia)).filter((d) => !isNaN(d));
    return dates.length ? new Date(Math.max(...dates)) : new Date();
  }, [data]);

  useEffect(() => {
    if (data.length && selectedYear === null && selectedMonth === null) {
      setSelectedYear(latestDate.getFullYear());
      setSelectedMonth(latestDate.getMonth()); // default: latest month
    }
  }, [latestDate, data, selectedYear, selectedMonth]);

  const availableYears = useMemo(() => {
    const years = new Set();
    data.forEach((item) => {
      const d = new Date(item.DataWystawienia);
      if (!isNaN(d)) years.add(d.getFullYear());
    });
    return Array.from(years).sort();
  }, [data]);

  const allContractors = useMemo(() => {
    if (selectedYear == null || selectedMonth == null) return [];

    const fullYear = selectedMonth === "full";
    const salesMap = {};
    const ytdPrevMap = {};

    data.forEach((item) => {
      const name = item.NazwaKontrahenta;
      const nip = item.NrKontrahenta || "Brak NIP";
      const date = new Date(item.DataWystawienia);
      const value = parseFloat(item.K_21 || 0);
      if (!name || isNaN(date) || isNaN(value)) return;

      const year = date.getFullYear();
      const month = date.getMonth();
      const id = `${name}-${nip}`;

      if (!salesMap[id]) {
        salesMap[id] = {
          id, name, nip,
          YTD: 0,
          currentMonth: 0,
          lastMonth: 0,
          lastYearSameMonth: 0,
        };
      }

      if (year === selectedYear) {
        salesMap[id].YTD += value;

        if (!fullYear && month === selectedMonth) {
          salesMap[id].currentMonth += value;
        }

        if (!fullYear) {
          const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
          const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
          if (month === prevMonth && year === prevYear) {
            salesMap[id].lastMonth += value;
          }
        }
      }

      // previous year YTD ranking basis
      if (year === selectedYear - 1) {
        const prevId = `${name}-${nip}`;
        if (!ytdPrevMap[prevId]) ytdPrevMap[prevId] = 0;
        ytdPrevMap[prevId] += value;

        if (!fullYear && month === selectedMonth) {
          salesMap[id].lastYearSameMonth =
            (salesMap[id].lastYearSameMonth || 0) + value;
        }
      }
    });

    // If "Cały rok" -> show YTD in "Bieżący miesiąc", and disable month-specific comparisons
    let contractors = Object.values(salesMap).map((c) => {
      if (fullYear) {
        return {
          ...c,
          currentMonth: c.YTD,
          lastMonth: 0,
          lastYearSameMonth: 0,
          momChange: null,
          yoyChange: null,
        };
      }
      return {
        ...c,
        momChange: c.lastMonth > 0 ? ((c.currentMonth - c.lastMonth) / c.lastMonth) * 100 : null,
        yoyChange:
          c.lastYearSameMonth > 0
            ? ((c.currentMonth - c.lastYearSameMonth) / c.lastYearSameMonth) * 100
            : null,
      };
    });

    // Rank by current year YTD
    contractors.sort((a, b) => b.YTD - a.YTD);
    contractors = contractors.map((c, i) => ({ ...c, rank: i + 1 }));

    // Previous-year rank map (by previous-year YTD)
    const prevRankMap = Object.entries(ytdPrevMap)
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [key], i) => {
        acc[key] = i + 1;
        return acc;
      }, {});

    contractors = contractors.map((c) => {
      const prevRank = prevRankMap[`${c.name}-${c.nip}`] || null;
      return { ...c, prevRank, rankChange: prevRank != null ? prevRank - c.rank : null };
    });

    return contractors;
  }, [data, selectedYear, selectedMonth]);

  const filtered = useMemo(
    () =>
      allContractors.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.nip.toLowerCase().includes(search.toLowerCase())
      ),
    [allContractors, search]
  );

  const sorted = useMemo(() => {
    const out = [...filtered];
    out.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortOrder === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [filtered, sortKey, sortOrder]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, currentPage]);

  const pageCount = Math.ceil(filtered.length / rowsPerPage);

  const summary = useMemo(() => {
    const sum = {
      YTD: 0,
      currentMonth: 0,
      lastMonth: 0,
      lastYearSameMonth: 0,
    };
    filtered.forEach((c) => {
      sum.YTD += c.YTD;
      sum.currentMonth += c.currentMonth;
      sum.lastMonth += c.lastMonth;
      sum.lastYearSameMonth += c.lastYearSameMonth;
    });
    const avg = {
      YTD: sum.YTD / (filtered.length || 1),
      currentMonth: sum.currentMonth / (filtered.length || 1),
      lastMonth: sum.lastMonth / (filtered.length || 1),
      lastYearSameMonth: sum.lastYearSameMonth / (filtered.length || 1),
    };
    return { sum, avg };
  }, [filtered]);

  const format = (val) =>
    val != null
      ? val
          .toFixed(2)
          .replace(".", ",")
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      : "–";

  const formatPercent = (val) => (val != null ? `${val.toFixed(1).replace(".", ",")}%` : "–");

  const handleExport = () => {
    const rows = sorted.map((row) => ({
      Nazwa: row.name,
      NIP: row.nip,
      YTD: row.YTD.toFixed(2).replace(".", ","),
      "Bieżący miesiąc": row.currentMonth.toFixed(2).replace(".", ","),
      "Poprz. miesiąc": row.lastMonth.toFixed(2).replace(".", ","),
      "% m/m-1": row.momChange == null ? "–" : `${row.momChange.toFixed(1).replace(".", ",")}%`,
      "Ten sam mies. (r-1)": row.lastYearSameMonth.toFixed(2).replace(".", ","),
      "% r/r-1": row.yoyChange == null ? "–" : `${row.yoyChange.toFixed(1).replace(".", ",")}%`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sprzedaż");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    const monthLabel =
      selectedMonth === "full" ? "caly_rok" : String((selectedMonth ?? 0) + 1).padStart(2, "0");

    saveAs(blob, `sprzedaz_kontrahenci_${selectedYear}_${monthLabel}.xlsx`);
  };

  if (selectedYear === null || selectedMonth === null) {
    return <div>Ładowanie danych...</div>;
  }

  return (
    <div>
      <ChartTitle>
        {/* 🔹 updated name */}
        Lista wszystkich kontrahentów wg wartości sprzedaży
        <InfoIconWrapper data-tooltip-id="sales-by-contractor-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="sales-by-contractor-tooltip"
        place="left"
        content={
          <>
            <strong>Kontrahenci wg sprzedaży</strong><br />
            Tabela pokazuje wartość sprzedaży wg kontrahenta,<br />
            z podziałem na miesiąc, m/m, r/r i sumę roczną.
          </>
        }
      />

      {/* Filters */}
      <Controls>
        <Select
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(parseInt(e.target.value, 10));
            setCurrentPage(1);
          }}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>

        <Select
          value={selectedMonth}
          onChange={(e) => {
            const val = e.target.value === "full" ? "full" : parseInt(e.target.value, 10);
            setSelectedMonth(val);
            setCurrentPage(1);
          }}
        >
          {/* 🔹 added “Cały rok” */}
          <option value="full">Cały rok</option>
          {monthNamesPL.map((m, idx) => (
            <option key={idx} value={idx}>{m}</option>
          ))}
        </Select>

        <SearchInput
          type="text"
          placeholder="Szukaj po nazwie lub NIP..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <ExportButton onClick={handleExport}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </Controls>

      {/* Table */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <StyledTable>
          <thead>
            <tr>
              {[
                ["name", "Nazwa"],
                ["currentMonth", "Wartość bieżący m-c"],
                ["lastMonth", "Wartość poprzedni m-c"],
                ["lastYearSameMonth", "Wartość r-1"],
                ["momChange", "m/m (%)"],
                ["yoyChange", "r/r (%)"],
                ["rank", "Ranking"],
                ["prevRank", "Ranking r-1"],
                ["rankChange", "Zmiana rankingu"],
                ["YTD", "YTD"],
              ].map(([key, label]) => (
                <th
                  key={key}
                  className={key === sortKey ? "sorted" : ""}
                  onClick={() => {
                    if (sortKey === key) {
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                    } else {
                      setSortKey(key);
                      setSortOrder("desc");
                    }
                  }}
                >
                  {label} {sortKey === key ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginated.map((c, idx) => (
              <tr key={idx}>
                <td title={c.name}>{c.name}</td>
                <td>{format(c.currentMonth)}</td>
                <td>{format(c.lastMonth)}</td>
                <td>{format(c.lastYearSameMonth)}</td>
                <td className={`bold ${c.momChange > 0 ? "positive" : c.momChange < 0 ? "negative" : ""}`}>
                  {formatPercent(c.momChange)}
                </td>
                <td className={`bold ${c.yoyChange > 0 ? "positive" : c.yoyChange < 0 ? "negative" : ""}`}>
                  {formatPercent(c.yoyChange)}
                </td>
                <td className={c.rank <= 3 ? "highlight-top" : c.rank >= 8 ? "highlight-low" : "highlight-neutral"}>
                  {c.rank}
                </td>
                <td className={c.prevRank <= 3 ? "highlight-top" : c.prevRank >= 8 ? "highlight-low" : "highlight-neutral"}>
                  {c.prevRank || "-"}
                </td>
                <td className={c.rankChange > 0 ? "rank-up" : c.rankChange < 0 ? "rank-down" : "rank-neutral"}>
                  {c.rankChange !== null
                    ? c.rankChange > 0
                      ? `↑ ${c.rankChange}`
                      : c.rankChange < 0
                      ? `↓ ${Math.abs(c.rankChange)}`
                      : "–"
                    : "-"}
                </td>
                <td>{format(c.YTD)}</td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <td style={footerStyle} colSpan={2}>Suma:</td>
              <td style={footerStyle}>{format(summary.sum.currentMonth)}</td>
              <td style={footerStyle}>{format(summary.sum.lastMonth)}</td>
              <td style={footerStyle}>{format(summary.sum.lastYearSameMonth)}</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>{format(summary.sum.YTD)}</td>
            </tr>
            <tr>
              <td style={footerStyle} colSpan={2}>Średnia:</td>
              <td style={footerStyle}>{format(summary.avg.currentMonth)}</td>
              <td style={footerStyle}>{format(summary.avg.lastMonth)}</td>
              <td style={footerStyle}>{format(summary.avg.lastYearSameMonth)}</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>–</td>
              <td style={footerStyle}>{format(summary.avg.YTD)}</td>
            </tr>
          </tfoot>
        </StyledTable>
      </div>

      {/* Pagination */}
      <div style={{ marginTop: "10px", textAlign: "center" }}>
        {pageCount > 1 && (
          <>
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              style={{
                margin: "0 4px",
                padding: "5px 10px",
                backgroundColor: "#eee",
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              «
            </button>

            {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
              let page = i + 1;
              if (currentPage > 3 && currentPage < pageCount - 1) {
                page = currentPage - 2 + i;
              } else if (currentPage >= pageCount - 1 && pageCount > 5) {
                page = pageCount - 4 + i;
              }
              if (page < 1 || page > pageCount) return null;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    margin: "0 4px",
                    padding: "5px 10px",
                    backgroundColor: page === currentPage ? "#87CEEB" : "#eee",
                    border: "1px solid #ccc",
                    cursor: "pointer",
                  }}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
              disabled={currentPage === pageCount}
              style={{
                margin: "0 4px",
                padding: "5px 10px",
                backgroundColor: "#eee",
                border: "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              »
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const cellStyle = {
  border: "1px solid #ccc",
  padding: "6px 8px",
  textAlign: "center",
};

const footerStyle = {
  ...cellStyle,
  fontWeight: "bold",
  backgroundColor: "#f0f0f0",
};

export default SalesByContractorTable;
